import React from 'react';
import { runtime } from '@do/lobster-roll-cli/runtime';
import { SuspenseCache } from '@do/lobster-roll-suspense-cache';

export type UseFlipper<K extends string> = (key: K) => boolean;

export interface UseFlippers<K extends string> {
  (keys: []): [];
  (keys: [K]): [boolean];
  (keys: [K, K]): [boolean, boolean];
  (keys: [K, K, K]): [boolean, boolean, boolean];
  (keys: [K, K, K, K]): [boolean, boolean, boolean, boolean];
  (keys: [K, K, K, K, K]): [boolean, boolean, boolean, boolean, boolean];
  (keys: [K, K, K, K, K, K]): [boolean, boolean, boolean, boolean, boolean, boolean];
  (keys: [K, K, K, K, K, K, K, K]): [boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean];
  (keys: K[]): boolean[];
}

export type FlippersEnabledFC<K extends string> = React.FC<
  React.PropsWithChildren<{ keys: K[]; mode?: 'every' | 'some' | 'none' }>
>;

const cache = new SuspenseCache<{ [name: string]: boolean }>();

/**
 * Fetches a flipper by its key.
 *
 * @example
 * const isFeatureEnabled = useFlipper('new-feature');
 */
export const useFlipper: UseFlipper<string> = (key: string): boolean => {
  const { data, meta } = runtime.dataBridge.use('flippers');

  if (key in data) return data[key];

  /**
   * If the flipper is not in the data, then we attempt to fetch it from the API.
   * This should _only_ happen in cases where the flipper has not been published with
   * the application manifest yet.
   */
  return cache.use(key, () => {
    console.warn(`@do/flippers: Flipper "${key}" not found in data, fetching from API.`);

    const path = meta.fetchTemplate.path.replace(meta.fetchTemplate.key, key);

    return fetch(path)
      .then((res) => res.json())
      .then((value) => value ?? false)
      .catch(() => false);
  });
};

/**
 * Fetches multiple flippers by their keys.
 *
 * @example
 * const [isFeatureAEnabled, isFeatureBEnabled] = useFlippers(['feature-a', 'feature-b']);
 */
export const useFlippers: UseFlippers<string> = (keys) => {
  return keys.map((name) => useFlipper(name)) as any;
};

/**
 * @private
 *
 * Internal utility to determine if any, all, or none of the specified flippers are enabled.
 */
const useFlippersWithMode = (keys: string[], mode: 'every' | 'some' | 'none') => {
  const flippers = useFlippers(keys);

  switch (mode) {
    case 'some':
      return flippers.some(Boolean);
    case 'none':
      return !flippers.some(Boolean);
    default: // 'every'
      return flippers.every(Boolean);
  }
};

/**
 * Renders children if the specified flippers are enabled.
 *
 * @example
 * <FlippersEnabled keys={["feature-a", "feature-b"]} mode="every">
 *   <NewFeatureComponent />
 * </FlippersEnabled>
 */
export const FlippersEnabled: FlippersEnabledFC<string> = (props) => {
  const { keys, children, mode = 'every' } = props;

  const show = useFlippersWithMode(keys, mode);

  if (show) {
    return <>{children}</>;
  }

  return null;
};
FlippersEnabled.displayName = 'FlippersEnabled';
